import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';
import '../../shipment/ShipmentsPage.css';
import '../payments/PaymentProviderDetailsPage.css';
import CreateBusinessHeader from '../../business/components/CreateBusinessHeader';
import PromotionActiveToggle from '../../promotion/components/PromotionActiveToggle';
import { CONFIGURABLE_RECEIPT_PROVIDERS } from '../../../types/ReceiptProviderTypes';
import { ProviderStatusTypes } from '../../../types/ProviderStatusTypes';

function isProviderActive(row) {
  return Number(row?.statusId) !== ProviderStatusTypes.INACTIVE;
}

function ReceiptProvidersPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { requestWithMeta } = useHttp();

  const [saved, setSaved] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [actionError, setActionError] = React.useState(null);
  const [togglingId, setTogglingId] = React.useState(null);

  React.useEffect(() => {
    if (!branchId || branchId === 'store') {
      setLoading(false);
      setError('Выберите филиал');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await requestWithMeta(
          `/operator/branches/${encodeURIComponent(branchId)}/receipt-providers`,
          'GET'
        );
        if (cancelled) return;
        if (!res?.ok) {
          setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить кассу');
          setSaved([]);
          return;
        }
        const list = Array.isArray(res?.data?.receiptProviders) ? res.data.receiptProviders : [];
        setSaved(list);
      } catch (_) {
        if (!cancelled) {
          setError('Ошибка загрузки');
          setSaved([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [branchId, requestWithMeta]);

  const savedByType = new Map(saved.map((row) => [Number(row.typeId), row]));

  const toggleStatus = async (row) => {
    const id = row?.id;
    if (id == null || togglingId !== null) return;

    const nextStatusId = isProviderActive(row)
      ? ProviderStatusTypes.INACTIVE
      : ProviderStatusTypes.ACTIVE;

    setTogglingId(id);
    setActionError(null);

    try {
      const res = await requestWithMeta(
        `/operator/receipt-providers/${encodeURIComponent(id)}/status`,
        'PUT',
        { statusId: nextStatusId }
      );

      if (!res?.ok) {
        setActionError(res?.data?.error || res?.data?.message || 'Не удалось изменить статус');
        return;
      }

      const updatedStatusId = res?.data?.receiptProvider?.statusId ?? nextStatusId;
      setSaved((prev) => prev.map((item) => (
        item.id === id ? { ...item, statusId: updatedStatusId } : item
      )));
    } catch (_) {
      setActionError('Ошибка изменения статуса');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="shipments-page-root">
      <CreateBusinessHeader
        title="Касса"
        subtitle=""
        onBack={() => navigate(`/branch/${branchId}/receipts`)}
      />

      <main className="shipments-page-content">
        {loading ? (
          <div className="shipments-page-loading" aria-busy="true">
            <div className="shipments-page-skeleton" />
            <div className="shipments-page-skeleton" />
          </div>
        ) : error ? (
          <div className="shipments-page-error">{error}</div>
        ) : (
          <>
            {actionError ? <div className="shipments-page-error">{actionError}</div> : null}
            <ul className="shipments-page-list" aria-label="Кассы">
              {CONFIGURABLE_RECEIPT_PROVIDERS.map((provider) => {
                const row = savedByType.get(provider.typeId) ?? null;
                const configured = row != null;
                const active = configured && isProviderActive(row);
                const isToggling = configured && togglingId !== null && togglingId === row.id;

                return (
                  <li key={provider.typeId} className="shipments-page-item">
                    <div className="shipments-page-itemRow">
                      <button
                        type="button"
                        className="shipments-page-itemMain payment-providers-itemMain"
                        onClick={() => navigate(`/branch/${branchId}/receipt-providers/${provider.path}`)}
                      >
                        <span className="shipments-page-itemName">{provider.name}</span>
                        <span className="shipments-page-itemPrice">
                          {configured ? 'Настроен' : 'Не настроен'}
                        </span>
                      </button>
                      {configured ? (
                        <PromotionActiveToggle
                          active={active}
                          loading={isToggling}
                          onToggle={(e) => {
                            e?.stopPropagation?.();
                            toggleStatus(row);
                          }}
                          label={active ? 'Нажмите, чтобы выключить' : 'Нажмите, чтобы включить'}
                        />
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </main>
    </div>
  );
}

export default ReceiptProvidersPage;
